import React, { useEffect } from 'react';
import {
  colors,
  Button,
  Select,
  Table,
  TableBody,
  TableHeader,
  TableHeading,
  TableRow,
  TextArea,
  useFirstRender,
  Tooltip,
  SearchActionBar,
} from '@insurancemenu/imenu-components-lib';
import DateRange from 'components/common/general/DateRange';
import { observer } from 'mobx-react-lite';
import { useRootStore } from 'store/rootStore';
import { toJS } from 'mobx';
import ActionModal from 'components/common/modals/ActionModal';
import { Reload, Download, Help } from '@insurancemenu/imenu-components-lib/dist/icons';
import { AlignmentTypes, ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { copyTextToBuffer } from 'global/utils/copyTextToBuffer';
import { createStyles } from './styles';
import FileExchangeRow from './FileExchangeRow';

const FileExchangeContent = observer((): JSX.Element => {
  const classes = createStyles();
  const firstRender = useFirstRender();

  const {
    fileExchangeStore: {
      files,
      totalElements,
      loading,
      totalPages,
      date: { from: dateFrom, to: dateTo },
      sponsorId,
      status,
      searchName,
      page,
      isWarningModalOpen,
      modalType,
      selectedFile,
      updateDate,
      setSponsorId,
      setPage,
      onSearch,
      onReset,
      setStatus,
      getFiles,
      getStatusOptions,
      downloadAllFiles,
      closeWarningModal,
      resendFile,
    },
    filesTrackingController: { showToast, getSponsorOptionsWithOptionAll },
  } = useRootStore();

  const convertedFiles = files.map((file) => toJS(file)).slice();
  const convertedSelectedFile = selectedFile ? toJS(selectedFile) : null;
  const errorMessageForSelectedFile =
    convertedSelectedFile?.failCause || 'The reason for the failure has not been specified.';

  useEffect(() => {
    void getFiles();
  }, []);

  useEffect(() => {
    void getFiles();
  }, [page]);

  useEffect(() => {
    if (!firstRender) {
      setPage(1);
      void getFiles();
    }
  }, [sponsorId, searchName, status, dateFrom, dateTo]);

  const rowsList: JSX.Element[] = convertedFiles.map((file) => {
    return <FileExchangeRow key={file.id} file={file} />;
  });

  const onCopyError = (): void => {
    copyTextToBuffer(errorMessageForSelectedFile);
    closeWarningModal();
  };
  const onFileResend = async (): Promise<void> => {
    if (convertedSelectedFile?.id) {
      await resendFile(convertedSelectedFile?.id);
    } else {
      showToast(
        'An issue has occurred while attempting to resend the file. Please try again later.',
        ToastColorTypes.WARNING,
      );
    }
    closeWarningModal();
  };

  const ErrorModalContent = (
    <div>
      <TextArea
        disabled
        name="failCause"
        value={errorMessageForSelectedFile}
        height="20rem"
        width="100%"
        className={classes.textArea}
      />
    </div>
  );

  const WarningModal = (): JSX.Element => {
    switch (modalType) {
      case 'RESEND_FILE':
        return (
          <ActionModal
            show={isWarningModalOpen}
            title="Re-send File"
            subtitle={
              <span>
                You are about to re-send
                <br />
                <b className={classes.selectedFile}>${convertedSelectedFile?.sftpPath || 'this'}</b> file.
              </span>
            }
            onClose={closeWarningModal}
            actionButtonText="Re-send"
            onAction={onFileResend}
            icon={<Reload width="5.6rem" color={colors.green[600]} />}
          />
        );
      case 'SHOW_ERROR':
        return (
          <ActionModal
            show={isWarningModalOpen}
            title="Error Message"
            subtitle="Copy the whole error message by clicking on the button below"
            modalContent={ErrorModalContent}
            onClose={closeWarningModal}
            actionButtonText="Copy Error"
            onAction={onCopyError}
          />
        );
      default:
        return <></>;
    }
  };

  return (
    <div className={classes.content}>
      <div className={classes.actionHeader}>
        <div className={classes.dateRange}>
          <DateRange updateTableByDateRange={updateDate} showAdminToggle={false} />
        </div>
        <div className={classes.filters}>
          <Select
            label="Sponsor Id"
            name="sponsorId"
            options={getSponsorOptionsWithOptionAll()}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
              setSponsorId(e.target.value);
            }}
            value={sponsorId}
            width="23rem"
          />
          <Select
            label="Status"
            name="status"
            options={getStatusOptions()}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
              setStatus(e.target.value);
            }}
            value={status}
            width="23rem"
          />
        </div>
      </div>
      <div className={classes.tableWrapper}>
        <SearchActionBar
          totalElements={totalElements}
          totalTitle="Total Files Count"
          searchPlaceholder="File path"
          isSearchButtonDisabled={loading}
          onSearch={onSearch}
          onReset={onReset}
        />
        <Table
          loading={loading}
          maxHeight="55rem"
          minHeight="55rem"
          totalElements={totalElements}
          pagination={{
            onPageChange: setPage,
            maxPage: totalPages || 1,
            tablePage: page,
          }}
          className={classes.table}
        >
          <TableHeader>
            <TableRow className={classes.headingsRow}>
              <TableHeading className={classes.tableHeading}>Sponsor Id</TableHeading>
              <TableHeading className={classes.tableHeading}>Date</TableHeading>
              <TableHeading className={classes.tableHeading}>File Path</TableHeading>
              <TableHeading className={classes.tableHeading}>Status</TableHeading>
              <TableHeading className={classes.tableHeading}>
                {totalElements > 0 && (
                  <div className={classes.downloadButtonWrapper}>
                    <Tooltip
                      alignment={AlignmentTypes.LEFT_CENTER}
                      className={classes.tooltipWrapper}
                      label={
                        <span>
                          Download Pulled/Sent files from all pages. <br /> Files with a &quot;Failed&quot; status will
                          not be included in the ZIP.
                        </span>
                      }
                    >
                      <Help width="2.5rem" height="2.5rem" color={colors.green[600]} className={classes.infoButton} />
                    </Tooltip>

                    <Button
                      leftIconProp={<Download />}
                      type="outlined"
                      color="primary"
                      className={classes.downloadAllButton}
                      disabled={loading}
                      onClick={downloadAllFiles}
                    >
                      Download All
                    </Button>
                  </div>
                )}
              </TableHeading>
            </TableRow>
          </TableHeader>
          <TableBody>{rowsList}</TableBody>
        </Table>
      </div>
      {WarningModal()}
    </div>
  );
});

export default FileExchangeContent;
